import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataTable,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableExpandedRow,
  TableExpandHeader,
  TableExpandRow,
  TableHead,
  TableHeader,
  TableRow,
  TableToolbar,
  TableToolbarContent
} from '@carbon/react';

import { routes } from 'core/routing';
import ActionButton from 'shared/components/ActionButton';
import TableExpandText from 'shared/components/data-table/TableExpandText';
import { formatDate } from 'utils/dates';
import styles from 'theme/features/landing/landing.module.scss';

const RecentSites = ({ sites }) => {
  const navigate = useNavigate();

  const tableData =
    sites?.map((x) => ({
      id: x.id?.toString(),
      address: x.address ?? 'Unknown',
      location: `${x.city}, ${x.country}`,
      createdOn: formatDate(x.createdOn),
      updatedOn: x.updatedOn ? formatDate(x.updatedOn) : '-',
      notes: x.notes
    })) ?? [];

  const headers = [
    { key: 'address', header: 'Address' },
    { key: 'location', header: 'Location' },
    { key: 'createdOn', header: 'Created On' },
    { key: 'updatedOn', header: 'Modified On' },
    { key: 'notes', header: 'Notes' }
  ];

  return (
    <DataTable
      headers={headers}
      rows={tableData}
      size="sm"
      render={({ rows, getTableContainerProps, getToolbarProps, getTableProps, getHeaderProps, getRowProps }) => (
        <TableContainer
          title="Your Recent Sites"
          description="The most recent sites you've interacted with. You can view the full list of sites through 'View All'"
          className={styles['table-header']}
          {...getTableContainerProps()}
        >
          <TableToolbar {...getToolbarProps()} size="sm">
            <TableToolbarContent>
              <ActionButton onClick={() => navigate(routes.allProjects())}>View All</ActionButton>
            </TableToolbarContent>
          </TableToolbar>
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                <TableExpandHeader id="expand" />
                {headers.map((header) => (
                  <TableHeader {...getHeaderProps({ header })} key={header.key}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => {
                const notes = sites[index]?.notes;

                return (
                  <Fragment key={row.id}>
                    {notes ? (
                      <>
                        <TableExpandRow expandHeader="expand" {...getRowProps({ row })}>
                          {row.cells.map((cell) => (
                            <TableCell key={cell.id}>{cell.value}</TableCell>
                          ))}
                        </TableExpandRow>
                        <TableExpandedRow colSpan={headers.length + 1}>
                          <TableExpandText>{notes}</TableExpandText>
                        </TableExpandedRow>
                      </>
                    ) : (
                      <TableRow {...getRowProps({ row })} key={row.id}>
                        <TableCell key="empty-expand" />
                        {row.cells.map((cell) => (
                          <TableCell key={cell.id}>{cell.value}</TableCell>
                        ))}
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    />
  );
};

export default RecentSites;
